import { useAppDataContext } from '../../../../framework/app/app.data.context';
import { AnalyticsConfig } from '../types/analytics.types';
import { useCallback } from 'react';
import { getBaseUrl } from '../../../../framework/config/api.config';

// Debug logging utility - only logs in development mode
const DEBUG = process.env.NODE_ENV === 'development';
const debugLog = (message: string, ...args: any[]) => {
  if (DEBUG) {
   // console.log(message, ...args);
  }
};

interface UseApiConfigProps {
  dashboardConfig?: AnalyticsConfig;
  dashboardKey?: string; // The actual dashboard.key for application routing
}

/**
 * Hook to get API configuration for analytics services
 */
export const useApiConfig = (props: UseApiConfigProps = {}) => {
  const { dashboardConfig, dashboardKey } = props;
  const context = useAppDataContext();
  
  debugLog(`🔧 [useApiConfig] Called with:`, {
    dashboardKey,
    configId: dashboardConfig?.id,
    configName: dashboardConfig?.name
  });
  
  // Use framework utility for consistent base URL resolution

  // Get application key from dashboard.key (preferred) or derive from config
  const getAppKey = (): string => {
    // Priority 1: Use explicit dashboard key if provided
    if (dashboardKey) {
      debugLog(`🔧 [useApiConfig] Using explicit dashboard key: ${dashboardKey}`);
      return dashboardKey;
    }
    
    // Priority 2: Derive from dashboard config ID (fallback for compatibility)
    if (dashboardConfig?.id) {
      let configId = dashboardConfig.id;
      debugLog(`🔧 [useApiConfig] Dashboard config ID: ${configId}`);
      
      // Defensive: Clean up any virtual dataset IDs that might have corrupted the config
      if (configId.includes('-config-') && configId.includes('-dataset')) {
        // Extract the original dashboard ID from corrupted virtual dataset ID
        configId = configId.split('-config-')[0];
        debugLog(`🔧 [useApiConfig] Cleaned corrupted config ID to: ${configId}`);
      } else if (configId.endsWith('-dataset')) {
        // Remove -dataset suffix if present
        configId = configId.replace('-dataset', '');
        debugLog(`🔧 [useApiConfig] Removed -dataset suffix, config ID: ${configId}`);
      }
      
      // Convert config ID to a valid app key format
      // e.g., "showcase-dashboard" -> "testdash" (for compatibility)
      const appKey = configId === 'showcase-dashboard' ? 'testdash' : configId;
      debugLog(`🔧 [useApiConfig] Derived app key from config: ${appKey}`);
      return appKey;
    }
    
    // Fallback to testdash for development/testing
    debugLog(`🔧 [useApiConfig] Using fallback app key: testdash`);
    return 'testdash';
  };

  // Get authentication token using the same pattern as the app
  const getAuthToken = useCallback(async (): Promise<string> => {
    try {
      // Use the same token provider that's available in app context
      const token = await context?.appContext?.tokenProvider?.getIdToken();
      return token || '';
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return '';
    }
  }, [context?.appContext?.tokenProvider]);

  return {
    baseUrl: getBaseUrl(),
    appKey: getAppKey(),
    configId: dashboardConfig?.id || 'showcase-dashboard',
    getAuthToken
  };
};
